import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Shield, Pencil, Power } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { usePermission, useAllowedActionsBatch, invalidatePermissionQueries } from '@/hooks/usePermission'
import { getRoles, createRole, toggleRole } from '@/services/api/permission.service'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import { RoleType } from '@/types/enums'


export function RolesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: roles, isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
  })
  const { can, isOwner, isAdmin } = usePermission()
  const canCreate = can(PERMISSION_KEYS.ROLE_CREATE)
  const roleActions = useAllowedActionsBatch('ROLE', useMemo(() => (roles ?? []).map((r) => r.id), [roles]))

  const toggleMutation = useMutation({
    mutationFn: toggleRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      invalidatePermissionQueries(queryClient)
      toast.success('Role updated')
    },
    onError: () => {
      toast.error('Failed to update role')
    },
  })

  const createMutation = useMutation({
    mutationFn: createRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      invalidatePermissionQueries(queryClient)
      toast.success('Role created')
    },
    onError: () => {
      toast.error('Failed to create role')
    },
  })

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [newRoleName, setNewRoleName] = useState('')
  const [newRoleType, setNewRoleType] = useState<RoleType>(RoleType.USER)

  const handleCreate = () => {
    createMutation.mutate(
      { name: newRoleName, roleType: newRoleType },
      {
        onSuccess: () => {
          setIsCreateDialogOpen(false)
          setNewRoleName('')
          setNewRoleType(RoleType.USER)
        },
      }
    )
  }

  const getRoleTypeBadge = (type: RoleType) => {
    switch (type) {
      case RoleType.ADMIN:
        return <Badge variant="default">Admin</Badge>
      case RoleType.USER:
        return <Badge variant="secondary">User</Badge>
      case RoleType.DEVELOPER:
        return <Badge variant="warning">Developer</Badge>
      default:
        return <Badge variant="outline">{type}</Badge>
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Roles & Permissions</h1>
          <p className="mt-1 text-neutral-500">
            Manage roles and their permissions
          </p>
        </div>

        {canCreate && (
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Role
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Role</DialogTitle>
              <DialogDescription>
                Create a new role for your workspace
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Role Name</Label>
                <Input
                  id="name"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  placeholder="e.g., Content Manager"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Role Type</Label>
                <Select value={newRoleType} onValueChange={(value) => setNewRoleType(value as RoleType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={RoleType.USER}>User</SelectItem>
                    {(isOwner || isAdmin) && <SelectItem value={RoleType.ADMIN}>Admin</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={!newRoleName.trim() || createMutation.isPending}>
                {createMutation.isPending ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </CardHeader>
            </Card>
          ))}
        </div>
      ) : roles?.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Shield className="h-12 w-12 text-neutral-300" />
            <h3 className="mt-4 text-lg font-medium">No roles yet</h3>
            <p className="mt-1 text-sm text-neutral-500">
              Create roles to manage permissions
            </p>
            {canCreate && (
              <Button className="mt-4" onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Role
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {roles?.map((role) => (
            <Link key={role.id} to={`/roles/${role.id}`} className="block">
              <Card variant="interactive">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{role.name}</CardTitle>
                      <CardDescription className="mt-1">
                        {role.description || 'No description'}
                      </CardDescription>
                    </div>
                    {getRoleTypeBadge(role.roleType)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <Badge variant={role.active ? 'success' : 'secondary'}>
                      {role.active ? 'Active' : 'Inactive'}
                    </Badge>
                    <div className="flex gap-1" onClick={(e) => e.preventDefault()}>
                      {roleActions.can(role.id, PERMISSION_KEYS.ROLE_UPDATE)
                        && (role.roleType !== RoleType.ADMIN || isOwner || isAdmin) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={role.active ? 'Deactivate role' : 'Activate role'}
                          onClick={(e) => {
                            e.preventDefault()
                            toggleMutation.mutate(role.id)
                          }}
                        >
                          <Power className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        onClick={(e) => {
                          e.preventDefault()
                          navigate(`/roles/${role.id}`)
                        }}
                      >
                        <span>
                          <Pencil className="h-4 w-4" />
                        </span>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}